// Subscription tier constants
export const SUBSCRIPTION_TIERS = {
    CREATOR: 'creator',
    CREATOR_PRO: 'creator-pro',
    AFFILIATE: 'affiliate',
} as const;

export type SubscriptionTier = (typeof SUBSCRIPTION_TIERS)[keyof typeof SUBSCRIPTION_TIERS];

export type PaidSubscriptionTier = 'creator' | 'creator-pro';

// Subscription interval constants
export const SUBSCRIPTION_INTERVALS = {
    MONTH: 'month',
    YEAR: 'year',
} as const;

export type SubscriptionInterval = (typeof SUBSCRIPTION_INTERVALS)[keyof typeof SUBSCRIPTION_INTERVALS];

// Subscription provider constants
export const SUBSCRIPTION_PROVIDERS = {
    STRIPE: 'stripe',
    REVENUE_CAT: 'revenue_cat',
} as const;

export type SubscriptionProvider = (typeof SUBSCRIPTION_PROVIDERS)[keyof typeof SUBSCRIPTION_PROVIDERS];

// Subscription status constants
export const SUBSCRIPTION_STATUS = {
    CANCELLED: 0,
    ACTIVE: 1,
    PENDING_ASSISTANCE: 2,
    DOWNGRADED: 3,
    UPGRADED: 4,
} as const;

export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUS)[keyof typeof SUBSCRIPTION_STATUS];

// Price data structure
export interface PriceData {
    amount: number;
    stripe_id: string;
}

export interface TierPrices {
    month: PriceData;
    year: PriceData;
}

export interface CountryPrices {
    currency: string;
    prices: Record<PaidSubscriptionTier, TierPrices>;
}

export interface SubscriptionPricesResponse {
    [countryCode: string]: CountryPrices;
}

// Estimate subscription change response
export interface EstimateSubscriptionChangeResponse {
    estimated_cost: number;
    start_date: number;
    status: 'upgrade' | 'downgrade' | 'interval_change';
}

// Change tier request
export interface ChangeTierRequest {
    stripe_id: string;
    price_id: string;
}

// Renew subscription request
export interface RenewSubscriptionRequest {
    stripe_id: string;
}
/**
 * Request payload for creating a new subscription
 * POST /v1/integrations/stripe/create-subscription
 */
export interface CreateSubscriptionRequest {
    priceId: string;
    customerId: string;
    paymentMethodId?: string;
    promoId?: string;
    trialDays?: number;
    mustApplyTrial?: boolean;
    idempotencyKey?: string;
}

/**
 * Stripe subscription response
 * Returned from subscription creation endpoint
 */
export interface StripeSubscriptionResponse {
    id: string;
    status: string;
    client_secret?: string;
    trial_end?: number;
    current_period_end?: number;
    cancel_at_period_end?: boolean;
    items?: {
        data: Array<{
            price: {
                id: string;
                unit_amount: number;
                currency: string;
                recurring: { interval: string };
            };
        }>;
    };
    latest_invoice?: {
        id?: string;
        payment_intent?: {
            client_secret?: string;
            status?: string;
        };
    };
}

/**
 * Promo code details from Stripe
 * GET /v1/integrations/stripe/get-promo-code-details/{code}
 */
export interface StripePromoCodeDetails {
    promo_code?: {
        id: string;
        code: string;
        active: boolean;
        max_redemptions?: number;
        times_redeemed?: number;
        coupon: {
            id: string;
            percent_off?: number;
            amount_off?: number;
            duration: string;
            metadata?: Record<string, string>;
        };
        metadata?: Record<string, string>;
    };
}
